import {useEffect, useRef, useState} from "react";
import axios, {CancelTokenSource} from "axios";
import {SortDescriptor} from "@progress/kendo-data-query";
import {ApiResponseDto, DropDownListItem, GridResult, NameWithId} from "@utils/Models";

import {ApiUrl_InkSeries_InkSeriesList, ApiUrl_OrderRecipe_Lookup} from "@utils/Endpoints";
import {useFlintNotificationContext} from "@components/page_components/common/notifications/FlintNotificationContext";
import {
    OrderRecipeLookupItemModel,
    OrderRecipeLookupRequest,
    OrderRecipeLookupResult,
} from "@pages/orders/preview/recipes/lookup/recipe-lookup.models";
import {OrderRecipeLookupPanelProps} from "@pages/orders/preview/recipes/lookup/OrderRecipeLookupPanel";

const INK_SERIES_FETCH_LIMIT = 5000;

function mergeInitialIntoOptions(initial: DropDownListItem, options: DropDownListItem[]): DropDownListItem[] {
    if (!initial?.Id) return options;
    if (options.some(o => o.Id === initial.Id)) return options;
    return [initial, ...options];
}

const PAGE_SIZE = 10;
const DEFAULT_SORT: SortDescriptor[] = [{field: "CreatedOn", dir: "desc"}];

export function useOrderRecipeLookupPanel(props: OrderRecipeLookupPanelProps) {
    const {showNotification} = useFlintNotificationContext();

    const [inkSerie, setInkSerie] = useState<DropDownListItem>(props.InitialInkSerie);
    const [substrate, setSubstrate] = useState<DropDownListItem>(props.InitialSubstrate);
    const [colorCode, setColorCode] = useState<string>(props.InitialColorCode ?? "");

    const [sort, setSort] = useState<SortDescriptor[]>(DEFAULT_SORT);
    const [items, setItems] = useState<OrderRecipeLookupItemModel[]>([]);
    const [total, setTotal] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [pagerKey, setPagerKey] = useState(0);

    const [inkSerieOptions, setInkSerieOptions] = useState<DropDownListItem[]>(
        () => mergeInitialIntoOptions(props.InitialInkSerie, [])
    );
    const [substrateOptions, setSubstrateOptions] = useState<DropDownListItem[]>(
        () => mergeInitialIntoOptions(props.InitialSubstrate, props.SubstrateOptions ?? [])
    );

    const cancelTokenRef = useRef<CancelTokenSource | null>(null);
    const inkSeriesCancelTokenRef = useRef<CancelTokenSource | null>(null);

    async function fetchInkSeriesList() {
        inkSeriesCancelTokenRef.current?.cancel("New ink series fetch");
        inkSeriesCancelTokenRef.current = axios.CancelToken.source();
        try {
            const res = await axios.post<ApiResponseDto<GridResult<NameWithId>>>(
                ApiUrl_InkSeries_InkSeriesList,
                {
                    GridPaging: {take: INK_SERIES_FETCH_LIMIT, skip: 0},
                    GridSorting: [{field: "Name", dir: "asc"}],
                },
                {cancelToken: inkSeriesCancelTokenRef.current.token}
            );
            const list = (res.data.Result?.Data ?? []).map<DropDownListItem>(s => ({Id: s.Id, Name: s.Name}));
            setInkSerieOptions(mergeInitialIntoOptions(props.InitialInkSerie, list));
        } catch (err: any) {
            if (!axios.isCancel(err)) showNotification(err);
        }
    }

    async function fetchPage(opts: {skip: number; sort: SortDescriptor[]; colorCode: string; inkSerie: DropDownListItem; substrate: DropDownListItem}) {
        if (opts.inkSerie?.Id <= 0 || opts.substrate?.Id <= 0) return;

        cancelTokenRef.current?.cancel("New lookup request");
        cancelTokenRef.current = axios.CancelToken.source();

        const body: OrderRecipeLookupRequest = {
            GridPaging: {skip: opts.skip, take: PAGE_SIZE},
            GridSorting: opts.sort.map(s => ({
                field: s.field,
                dir: (s.dir ?? "asc").toUpperCase() as "ASC" | "DESC",
            })),
            InkSeriesId: opts.inkSerie.Id,
            SubstrateId: opts.substrate.Id,
            ColorCode: opts.colorCode.trim() || null,
            ShowAllLabs: false,
            ShowAllDivisions: false,
        };

        setIsLoading(true);
        try {
            const res = await axios.post<ApiResponseDto<OrderRecipeLookupResult>>(
                ApiUrl_OrderRecipe_Lookup,
                body,
                {cancelToken: cancelTokenRef.current.token}
            );
            setItems(res.data.Result?.Data ?? []);
            setTotal(res.data.Result?.Total ?? 0);
        } catch (err: any) {
            if (!axios.isCancel(err)) showNotification(err);
        } finally {
            setIsLoading(false);
        }
    }

    // Initial fetch on mount — subsequent filter edits require the Search button.
    // Cancellation cleanup lives on the same effect so StrictMode's setup→cleanup→setup
    // re-fires the fetch after cancelling the first attempt (a separate cleanup effect
    // would cancel the fetch but leave a guarded init effect unable to refire).
    useEffect(() => {
        void fetchPage({skip: 0, sort: DEFAULT_SORT, colorCode, inkSerie, substrate});
        void fetchInkSeriesList();
        return () => {
            cancelTokenRef.current?.cancel("Lookup popup closed");
            inkSeriesCancelTokenRef.current?.cancel("Lookup popup closed");
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function handleSearchClicked() {
        setSort(DEFAULT_SORT);
        setPagerKey(k => k + 1);
        void fetchPage({skip: 0, sort: DEFAULT_SORT, colorCode, inkSerie, substrate});
    }

    function handlePageChange(page: number) {
        const newSkip = (page - 1) * PAGE_SIZE;
        void fetchPage({skip: newSkip, sort, colorCode, inkSerie, substrate});
    }

    function handleSortChange(newSort: SortDescriptor[]) {
        const effective = newSort.length > 0 ? newSort : DEFAULT_SORT;
        setSort(effective);
        setPagerKey(k => k + 1);
        void fetchPage({skip: 0, sort: effective, colorCode, inkSerie, substrate});
    }

    function handleInkSerieChanged(value: DropDownListItem) {
        setInkSerie(value);
        setSort(DEFAULT_SORT);
        setPagerKey(k => k + 1);
        void fetchPage({skip: 0, sort: DEFAULT_SORT, colorCode, inkSerie: value, substrate});
    }
    function handleSubstrateChanged(value: DropDownListItem) {
        setSubstrate(value);
        setSort(DEFAULT_SORT);
        setPagerKey(k => k + 1);
        void fetchPage({skip: 0, sort: DEFAULT_SORT, colorCode, inkSerie, substrate: value});
    }
    function handleColorCodeChanged(value: string) { setColorCode(value); }

    function handleResetClicked() {
        const initialInkSerie = props.InitialInkSerie;
        const initialSubstrate = props.InitialSubstrate;
        const initialColorCode = props.InitialColorCode ?? "";
        setInkSerie(initialInkSerie);
        setSubstrate(initialSubstrate);
        setColorCode(initialColorCode);
        setSort(DEFAULT_SORT);
        setPagerKey(k => k + 1);
        void fetchPage({skip: 0, sort: DEFAULT_SORT, colorCode: initialColorCode, inkSerie: initialInkSerie, substrate: initialSubstrate});
    }

    const isSearchEnabled = inkSerie?.Id > 0 && substrate?.Id > 0 && !isLoading;

    return {
        state: {
            inkSerie: {get: inkSerie},
            substrate: {get: substrate},
            colorCode: {get: colorCode},
            inkSerieOptions: {get: inkSerieOptions},
            substrateOptions: {get: substrateOptions},
            items: {get: items},
            total: {get: total},
            sort: {get: sort},
            pagerKey: {get: pagerKey},
            isLoading: {get: isLoading},
            pageSize: PAGE_SIZE,
        },
        handlers: {
            handleSearchClicked,
            handleResetClicked,
            handlePageChange,
            handleSortChange,
            handleInkSerieChanged,
            handleSubstrateChanged,
            handleColorCodeChanged,
            handleClose: props.OnClosed,
        },
        utilities: {
            isSearchEnabled,
        },
    };
}
